/**
 * ⚠️  INTERNAL USE ONLY — NOT FOR PUBLIC ACCESS
 * Route: /admin/reviews
 * Password-gated. noindex. Not linked from any public page.
 * Manual Google review request queue — send SMS after repairs are complete.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Home, RefreshCw, MessageSquare,
  Send, Ban, CheckCircle2, Clock, RotateCcw, AlertCircle, X,
  MapPin, Wrench, Phone, Calendar, Hash,
} from '../../lib/icons';
import SEOHead from '../../components/SEOHead';
import { AdminLoginGate, isAdminAuthed } from '../../components/AdminLoginGate';

// ── Types ─────────────────────────────────────────────────────────────────────
interface ReviewRequest {
  id:               string;
  customer_name:    string;
  customer_phone:   string;
  customer_email:   string | null;
  location_id:      string;
  location_name:    string;
  primary_service:  string;
  appointment_date: string | null;
  appointment_time: string | null;
  ticket_id:        number | null;
  ticket_number:    number | null;
  status:           'pending' | 'sent' | 'do_not_send';
  sent_at:          string | null;
  sent_message:     string | null;
  admin_notes:      string | null;
  created_at:       string;
  updated_at:       string;
}

type StatusFilter = 'all' | 'pending' | 'sent' | 'do_not_send';


// ── Helpers ───────────────────────────────────────────────────────────────────
const API_BASE = import.meta.env.DEV ? 'http://localhost:3001' : '';

async function fetchRequests(status?: StatusFilter, location_id?: string): Promise<{ requests: ReviewRequest[]; total: number }> {
  const params = new URLSearchParams({ limit: '200', offset: '0' });
  if (status && status !== 'all') params.set('status', status);
  if (location_id)               params.set('location_id', location_id);
  const res = await fetch(`${API_BASE}/api/review-requests?${params}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function postAction(body: {
  action: 'send' | 'mark_sent' | 'mark_do_not_send' | 'mark_pending';
  request_id: string;
  force?: boolean;
}): Promise<{ success: boolean; error?: string }> {
  const res = await fetch(`${API_BASE}/api/review-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
  return data;
}

const fmtDateTime = (s: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return s; }
};

const LOC_LABELS: Record<string, string> = {
  'san-diego': 'San Diego',
  'temecula':  'Temecula',
};

const STATUS_CONFIG = {
  pending:     { label: 'Pending',     color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40' },
  sent:        { label: 'Sent',        color: 'bg-green-500/20  text-green-300  border-green-500/40'  },
  do_not_send: { label: 'Do Not Send', color: 'bg-gray-500/20   text-gray-400   border-gray-500/40'   },
};

// ── Request Row ───────────────────────────────────────────────────────────────
function RequestRow({
  req,
  onAction,
  busy,
}: {
  req: ReviewRequest;
  onAction: (action: 'send' | 'mark_sent' | 'mark_do_not_send' | 'mark_pending', force?: boolean) => void;
  busy: boolean;
}) {
  const sc = STATUS_CONFIG[req.status] ?? STATUS_CONFIG.pending;

  return (
    <tr className="border-b border-gray-800/60 hover:bg-gray-900/30 transition-colors">
      {/* Customer */}
      <td className="px-4 py-3">
        <div className="text-sm font-medium text-[#E0E0E0]">{req.customer_name}</div>
        <div className="flex items-center gap-1 mt-0.5 text-gray-400 text-xs">
          <Phone className="h-3 w-3" />
          {req.customer_phone}
        </div>
      </td>

      {/* Location */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 text-sm text-gray-300">
          <MapPin className="h-3.5 w-3.5 text-gray-500 shrink-0" />
          {LOC_LABELS[req.location_id] ?? req.location_id}
        </div>
      </td>

      {/* Service */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 text-sm text-gray-300">
          <Wrench className="h-3.5 w-3.5 text-gray-500 shrink-0" />
          <span className="truncate max-w-[160px]">{req.primary_service}</span>
        </div>
      </td>

      {/* Appointment */}
      <td className="px-4 py-3 text-sm text-gray-400">
        {req.appointment_date ? (
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5 text-gray-500" />
            {req.appointment_date}
          </div>
        ) : '—'}
      </td>

      {/* Ticket */}
      <td className="px-4 py-3">
        {req.ticket_number ? (
          <div className="flex items-center gap-1 text-sm text-gray-400">
            <Hash className="h-3.5 w-3.5 text-gray-500" />
            {req.ticket_number}
          </div>
        ) : <span className="text-gray-600 text-xs">—</span>}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sc.color}`}>
          {req.status === 'sent' && <CheckCircle2 className="h-3 w-3" />}
          {req.status === 'pending' && <Clock className="h-3 w-3" />}
          {req.status === 'do_not_send' && <Ban className="h-3 w-3" />}
          {sc.label}
        </span>
        {req.sent_at && (
          <div className="text-xs text-gray-500 mt-0.5">{fmtDateTime(req.sent_at)}</div>
        )}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1.5">
          {req.status === 'pending' && (
            <>
              <button
                onClick={() => onAction('send')}
                disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-600/20 hover:bg-blue-600/40 text-blue-300 text-xs font-medium border border-blue-600/30 transition-colors disabled:opacity-40"
              >
                <Send className="h-3 w-3" /> Send SMS
              </button>
              <button
                onClick={() => onAction('mark_sent')}
                disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-green-600/20 hover:bg-green-600/40 text-green-300 text-xs font-medium border border-green-600/30 transition-colors disabled:opacity-40"
              >
                <CheckCircle2 className="h-3 w-3" /> Mark Sent
              </button>
              <button
                onClick={() => onAction('mark_do_not_send')}
                disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-600/20 hover:bg-gray-600/40 text-gray-400 text-xs font-medium border border-gray-600/30 transition-colors disabled:opacity-40"
              >
                <Ban className="h-3 w-3" /> Skip
              </button>
            </>
          )}

          {req.status === 'sent' && (
            <>
              <button
                onClick={() => onAction('send', true)}
                disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-orange-600/20 hover:bg-orange-600/40 text-orange-300 text-xs font-medium border border-orange-600/30 transition-colors disabled:opacity-40"
              >
                <Send className="h-3 w-3" /> Resend
              </button>
              <button
                onClick={() => onAction('mark_pending')}
                disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-700/30 hover:bg-gray-700/50 text-gray-400 text-xs font-medium border border-gray-700/40 transition-colors disabled:opacity-40"
              >
                <RotateCcw className="h-3 w-3" /> Reset
              </button>
            </>
          )}

          {req.status === 'do_not_send' && (
            <button
              onClick={() => onAction('mark_pending')}
              disabled={busy}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-700/30 hover:bg-gray-700/50 text-gray-400 text-xs font-medium border border-gray-700/40 transition-colors disabled:opacity-40"
            >
              <RotateCcw className="h-3 w-3" /> Reset to Pending
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
function ReviewRequestsDashboard() {
  const [requests, setRequests]   = useState<ReviewRequest[]>([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [locFilter, setLocFilter] = useState<string>('');
  const [busyIds, setBusyIds]     = useState<Set<string>>(new Set());
  const [toast, setToast]         = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRequests(statusFilter, locFilter || undefined);
      setRequests(data.requests);
      setTotal(data.total);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, locFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = useCallback(async (
    requestId: string,
    action: 'send' | 'mark_sent' | 'mark_do_not_send' | 'mark_pending',
    force = false,
  ) => {
    setBusyIds(s => new Set(s).add(requestId));
    try {
      await postAction({ action, request_id: requestId, force });
      const labels: Record<string, string> = {
        send:            'SMS sent successfully',
        mark_sent:       'Marked as sent',
        mark_do_not_send:'Marked as Do Not Send',
        mark_pending:    'Reset to pending',
      };
      showToast(labels[action] ?? 'Done', true);
      await load();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : String(e), false);
    } finally {
      setBusyIds(s => { const n = new Set(s); n.delete(requestId); return n; });
    }
  }, [load]);

  // Stats derived from all requests (only accurate within current filter context)
  const pendingCount     = requests.filter(r => r.status === 'pending').length;
  const sentCount        = requests.filter(r => r.status === 'sent').length;
  const doNotSendCount   = requests.filter(r => r.status === 'do_not_send').length;
  const sentTodayCount   = requests.filter(r => {
    if (r.status !== 'sent' || !r.sent_at) return false;
    const d = new Date(r.sent_at);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  }).length;

  const TAB_FILTERS: { id: StatusFilter; label: string }[] = [
    { id: 'pending',     label: 'Pending' },
    { id: 'sent',        label: 'Sent' },
    { id: 'do_not_send', label: 'Skipped' },
    { id: 'all',         label: 'All' },
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      <SEOHead
        title="Review Requests — Admin"
        description=""
        noindex
      />

      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-gray-500 hover:text-gray-300 transition-colors">
              <Home className="h-4 w-4" />
            </Link>
            <span className="text-gray-700">/</span>
            <MessageSquare className="h-5 w-5 text-[#FF1744]" />
            <h1 className="font-bold text-[#E0E0E0]">Review Requests</h1>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm transition-colors disabled:opacity-40"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Pending',      value: pendingCount,   icon: Clock,         color: 'text-yellow-400' },
            { label: 'Sent Today',   value: sentTodayCount, icon: Send,          color: 'text-blue-400'   },
            { label: 'Total Sent',   value: sentCount,      icon: CheckCircle2,  color: 'text-green-400'  },
            { label: 'Do Not Send',  value: doNotSendCount, icon: Ban,           color: 'text-gray-500'   },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`h-4 w-4 ${color}`} />
                <span className="text-xs text-gray-500">{label}</span>
              </div>
              <div className="text-2xl font-bold text-[#E0E0E0]">{value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Status tabs */}
          <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-xl p-1">
            {TAB_FILTERS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setStatusFilter(id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === id
                    ? 'bg-[#FF1744] text-white'
                    : 'text-gray-400 hover:text-[#E0E0E0]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Location filter */}
          <select
            value={locFilter}
            onChange={e => setLocFilter(e.target.value)}
            className="bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-gray-600"
          >
            <option value="">All Locations</option>
            <option value="san-diego">San Diego</option>
            <option value="temecula">Temecula</option>
          </select>

          <span className="text-xs text-gray-500 ml-auto">
            {total} result{total !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-900/20 border border-red-800/40 rounded-xl px-4 py-3 text-red-400 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-300">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500 text-sm gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500 gap-2">
              <MessageSquare className="h-8 w-8 text-gray-700" />
              <span className="text-sm">No review requests</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800 text-xs text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-left">Location</th>
                    <th className="px-4 py-3 text-left">Service</th>
                    <th className="px-4 py-3 text-left">Appt.</th>
                    <th className="px-4 py-3 text-left">Ticket</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(req => (
                    <RequestRow
                      key={req.id}
                      req={req}
                      busy={busyIds.has(req.id)}
                      onAction={(action, force) => handleAction(req.id, action, force)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium border transition-all ${
          toast.ok
            ? 'bg-green-900/90 border-green-700/50 text-green-200'
            : 'bg-red-900/90   border-red-700/50   text-red-200'
        }`}>
          {toast.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ── Page Root ─────────────────────────────────────────────────────────────────
export default function AdminReviewRequestsPage() {
  const [authed, setAuthed] = useState(isAdminAuthed());
  return authed
    ? <ReviewRequestsDashboard />
    : <AdminLoginGate onAuth={() => setAuthed(true)} />;
}
